import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/auth";

export interface TariffRow {
    tariffCode: string;
    dateFrom: string;
    feeBase: string;
    feeMin: string;
    feeMax: string;
    feePcnt: string;
    customize?: boolean;
}

interface Props {
    data?: TariffRow[];
    onChange: (data: TariffRow[]) => void;
    readonly?: boolean;
    tariffPlan?: string;
}

const DEFAULT_TARIFFS = [
    "IRO_ISV_S_LOCAL",
    "IRO_ISV_S_USD",
    "ISR_S_LOCAL",
    "ISR_S_USD"
];

const PLAN_TARIFFS = [
    "RETAIL_S_AMEX_1_LOCAL",
    "RETAIL_S_AMEX_1_USD",
    "RETAIL_S_MC_1_LOCAL",
    "RETAIL_S_MC_1_USD",
    "RETAIL_S_VISA_1_LOCAL",
    "RETAIL_S_VISA_1_USD"
];

const GLOABL_TARIFFS = [
    "RETAIL_S_MC_1_LOCAL",
    "RETAIL_S_VISA_1_LOCAL",
    "RETAIL_S_CLAVE_1_LOCAL",
    "ITBMS_W",
    "TCITBMS"
];

const EXTENDIDO_TARIFFS = [
    "RETAIL_S_AMEX_1_LOCAL_AS_ON_US",
    "RETAIL_S_AMEX_1_LOCAL_AS_DOMESTIC",
    "RETAIL_S_AMEX_1_LOCAL_AS_INTERNATIONAL",
    "RETAIL_S_AMEX_1_USD_AS_ON_US",
    "RETAIL_S_AMEX_1_USD_AS_DOMESTIC",
    "RETAIL_S_AMEX_1_USD_AS_INTERNATIONAL",

    "RETAIL_S_MC_1_LOCAL_AS_ON_US",
    "RETAIL_S_MC_1_LOCAL_AS_DOMESTIC",
    "RETAIL_S_MC_1_LOCAL_AS_INTERNATIONAL",
    "RETAIL_S_MC_1_USD_AS_ON_US",
    "RETAIL_S_MC_1_USD_AS_DOMESTIC",
    "RETAIL_S_MC_1_USD_AS_INTERNATIONAL",

    "RETAIL_S_VISA_1_LOCAL_AS_ON_US",
    "RETAIL_S_VISA_1_LOCAL_AS_DOMESTIC",
    "RETAIL_S_VISA_1_LOCAL_AS_INTERNATIONAL",
    "RETAIL_S_VISA_1_USD_AS_ON_US",
    "RETAIL_S_VISA_1_USD_AS_DOMESTIC",
    "RETAIL_S_VISA_1_USD_AS_INTERNATIONAL"
];

export const DeviceTariffForm: React.FC<Props> = ({
    data = [],
    onChange,
    readonly,
    tariffPlan
}) => {

    const { user } = useContext(AuthContext);
    const [rows, setRows] = useState<TariffRow[]>([]);

    // Estado visual del checkbox (independiente de "customize" que se envía)
    const [checkedMap, setCheckedMap] = useState<Record<string, boolean>>({});

    // Tarifas que ya venían personalizadas en la terminal existente: code -> feePcnt original
    const [originalValues, setOriginalValues] = useState<Record<string, string>>({});

    const isReadonly = readonly === true;

    useEffect(() => {

        const today = new Date().toISOString().split("T")[0];

        let planTariffs: string[] = [];
        let allTariffs: string[] = [];

        const isLafisePanama =
            user?.country?.code === "PAN" &&
            user?.banks?.[0]?.code === "0054";

        const isGlobalPanama =
            user?.country?.code === "PAN" &&
            user?.banks?.[0]?.code === "0052";

        const isPanama = user?.country?.code === "PAN";

        if (isLafisePanama) {
            if (tariffPlan?.toLowerCase().includes("extendido")) {
                planTariffs = EXTENDIDO_TARIFFS.filter(t => !t.includes("AMEX"));
            } else {
                planTariffs = PLAN_TARIFFS.filter(t => !t.includes("AMEX"));
            }
            allTariffs = [...planTariffs];
        } else if (isGlobalPanama) {
            allTariffs = [...GLOABL_TARIFFS];
        } else {
            if (tariffPlan === "TARIFF_PLAN_EXTENDIDO") {
                planTariffs = EXTENDIDO_TARIFFS;
            }

            if (tariffPlan === "PRELIMINARY_TARIFF_PLAN") {
                planTariffs = PLAN_TARIFFS;
            }

            allTariffs = [...DEFAULT_TARIFFS, ...planTariffs];
        }

        if (isPanama) {
            allTariffs = allTariffs.filter(t => !t.includes("USD"));
        }

        const originals: Record<string, string> = {};
        const checks: Record<string, boolean> = {};

        const initialized = allTariffs.map(code => {

            const saved = data.find(t => t.tariffCode === code);

            // SOLO PERSONALIZA SI YA VENÍA VALOR
            const wasCustomized = saved?.customize ?? !!saved?.feePcnt;

            // Registrar tarifas existentes en la terminal
            if (saved && wasCustomized) {
                originals[code] = saved.feePcnt ?? "0";
            }

            checks[code] = wasCustomized;

            return {
                tariffCode: code,
                dateFrom: saved?.dateFrom ?? today,
                feeBase: saved?.feeBase ?? "0",
                feeMin: saved?.feeMin ?? "0",
                feeMax: saved?.feeMax ?? "0",

                // VACÍO POR DEFECTO
                feePcnt: saved?.feePcnt ?? "",

                customize: wasCustomized
            };

        });

        setOriginalValues(originals);
        setCheckedMap(checks);
        setRows(initialized);
        onChange(initialized);

    }, [tariffPlan]);

    const updateValue = (
        code: string,
        field: keyof TariffRow,
        value: any
    ) => {

        if (isReadonly) return;

        const updated = rows.map(r =>
            r.tariffCode === code
                ? { ...r, [field]: value }
                : r
        );

        setRows(updated);
        onChange(updated);
    };

    const handleToggle = (code: string, checked: boolean) => {

        if (isReadonly) return;

        const existed = code in originalValues;

        setCheckedMap(prev => ({ ...prev, [code]: checked }));

        const updated = rows.map(r => {

            if (r.tariffCode !== code) {
                return r;
            }

            if (checked) {
                return {
                    ...r,
                    customize: true,
                    // si ya existía, restaurar su valor original
                    feePcnt: existed ? originalValues[code] : r.feePcnt
                };
            }

            // Desmarcado
            return existed
                // Terminal existente: tarifa en 0 y customize en true
                ? { ...r, customize: true, feePcnt: "0" }
                // Tarifa nueva: limpiar valor
                : { ...r, customize: false, feePcnt: "" };

        });

        setRows(updated);
        onChange(updated);
    };

    const getTarriffName = (code: string) => {

        switch (code) {
            case "IRO_ISV_S_LOCAL":
                return "Tasa ISV Local";
            case "IRO_ISV_S_USD":
                return "Tasa ISV USD";
            case "ISR_S_LOCAL":
                return "Tasa ISR Local";
            case "ISR_S_USD":
                return "Tasa ISR USD";
            case "RETAIL_S_AMEX_1_LOCAL":
                return "Tasa Amex Local";
            case "RETAIL_S_AMEX_1_USD":
                return "Tasa Amex USD";
            case "RETAIL_S_MC_1_LOCAL":
                return "Tasa MC Local";
            case "RETAIL_S_MC_1_USD":
                return "Tasa MC USD";
            case "RETAIL_S_VISA_1_LOCAL":
                return "Tasa Visa Local";
            case "RETAIL_S_VISA_1_USD":
                return "Tasa Visa USD";
            case "RETAIL_S_AMEX_1_LOCAL_AS_ON_US":
                return "Tasa Amex Local AS ON US";
            case "RETAIL_S_AMEX_1_LOCAL_AS_DOMESTIC":
                return "Tasa Amex Local AS Domestic";
            case "RETAIL_S_AMEX_1_LOCAL_AS_INTERNATIONAL":
                return "Tasa Amex Local AS International";
            case "RETAIL_S_AMEX_1_USD_AS_ON_US":
                return "Tasa Amex USD AS ON US";
            case "RETAIL_S_AMEX_1_USD_AS_DOMESTIC":
                return "Tasa Amex USD AS Domestic";
            case "RETAIL_S_AMEX_1_USD_AS_INTERNATIONAL":
                return "Tasa Amex USD AS International";
            case "RETAIL_S_MC_1_LOCAL_AS_ON_US":
                return "Tasa MC Local AS ON US";
            case "RETAIL_S_MC_1_LOCAL_AS_DOMESTIC":
                return "Tasa MC Local AS Domestic";
            case "RETAIL_S_MC_1_LOCAL_AS_INTERNATIONAL":
                return "Tasa MC Local AS International";
            case "RETAIL_S_MC_1_USD_AS_ON_US":
                return "Tasa MC USD AS ON US";
            case "RETAIL_S_MC_1_USD_AS_DOMESTIC":
                return "Tasa MC USD AS Domestic";
            case "RETAIL_S_MC_1_USD_AS_INTERNATIONAL":
                return "Tasa MC USD AS International";
            case "RETAIL_S_VISA_1_LOCAL_AS_ON_US":
                return "Tasa Visa Local AS ON US";
            case "RETAIL_S_VISA_1_LOCAL_AS_DOMESTIC":
                return "Tasa Visa Local AS Domestic";
            case "RETAIL_S_VISA_1_LOCAL_AS_INTERNATIONAL":
                return "Tasa Visa Local AS International";
            case "RETAIL_S_VISA_1_USD_AS_ON_US":
                return "Tasa Visa USD AS ON US";
            case "RETAIL_S_VISA_1_USD_AS_DOMESTIC":
                return "Tasa Visa USD AS Domestic";
            case "RETAIL_S_VISA_1_USD_AS_INTERNATIONAL":
                return "Tasa Visa USD AS International";
            case "RETAIL_S_CLAVE_1_LOCAL":
                return "Tasa Clave";
            case "ITBMS_W":
                return "Retención Afiliado";
            case "TCITBMS":
                return "ITBMS Comisión";

            default:
                return code;
        }

    };

    const isISR = (code: string) => {
        return code === "ISR_S_LOCAL" || code === "ISR_S_USD";
    };

    return (

        <div className="mb-4">

            <table className="table table-bordered">

                <thead className="table-light">
                    <tr>
                        <th>Tarifa</th>
                        <th>Personalizar</th>
                        <th>Tasa %</th>
                    </tr>
                </thead>

                <tbody>

                    {rows.map(row => {

                        const isChecked = checkedMap[row.tariffCode] === true;

                        return (

                            <tr key={row.tariffCode}>

                                <td className="text-muted">
                                    {getTarriffName(row.tariffCode)}
                                </td>

                                <td className="text-center">

                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        disabled={isReadonly}
                                        onChange={e =>
                                            handleToggle(row.tariffCode, e.target.checked)
                                        }
                                    />

                                </td>

                                <td>

                                    {isISR(row.tariffCode) ? (

                                        <select
                                            className="form-select"
                                            value={row.feePcnt}
                                            disabled={!isChecked || isReadonly}
                                            onChange={e =>
                                                updateValue(
                                                    row.tariffCode,
                                                    "feePcnt",
                                                    e.target.value
                                                )
                                            }
                                        >

                                            <option value="">
                                                Seleccione
                                            </option>

                                            <option value="0">
                                                Exento 0%
                                            </option>

                                            <option value="1.76">
                                                Impuesto 1.76%
                                            </option>

                                        </select>

                                    ) : (

                                        <div className="input-group">

                                            <input
                                                type="number"
                                                className="form-control"
                                                value={row.feePcnt}
                                                disabled={!isChecked || isReadonly}
                                                step="0.01"
                                                placeholder="Ingrese tasa"
                                                onWheel={e => (e.target as HTMLInputElement).blur()}
                                                onChange={e => {

                                                    const value = e.target.value.replace(",", ".");

                                                    updateValue(
                                                        row.tariffCode,
                                                        "feePcnt",
                                                        value
                                                    );

                                                }}
                                            />

                                            <span className="input-group-text">
                                                %
                                            </span>

                                        </div>

                                    )}

                                </td>

                            </tr>

                        );

                    })}

                </tbody>

            </table>

        </div>

    );

};
